<template>
  <v-container>
    <!-- this can be replaced with DialogConfirm at some point I think -->
    <v-dialog
      ref="warning-modal"
      v-model="showWarningModal"
      width="800"
      @click:outside="resolveWarningModal(false)">
      <v-card>
        <v-card-title>
          {{ $tc('warning') }}
        </v-card-title>
        <v-card-text>
          <p>{{ $t('product_start_temperature_warning_preamble') }}</p>
          <div v-for="(temp, index) in warningTemps" :key="index" class="mt-2">
            <v-row dense class="my-1">
              <v-col cols="3" class="text-subtitle-2">{{ $tc('product', 1) }}:</v-col>
              <v-col>
                <router-link :to="`/product/${temp.product.id}`" target="_blank">
                  {{ temp.product.name }} <v-icon color="primary">mdi-open-in-new</v-icon>
                </router-link>
              </v-col>
            </v-row>
            <v-row dense class="my-1">
              <v-col cols="3" class="text-subtitle-2">{{ $tc('temperature_range') }}:</v-col>
              <v-col>
                {{
                  $t('temperature_range_formatted', {
                    from: temp.product.min_temp,
                    to: temp.product.max_temp,
                    format: temp.product.temperature_scale
                  })
                }}
              </v-col>
            </v-row>
            <v-row dense class="my-1">
              <v-col cols="3" class="text-subtitle-2">{{ $tc('entered') }}:</v-col>
              <v-col class="text-warning">
                {{ temp.new_start_temp }}&deg;{{ temp.product.temperature_scale }}
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn @click="resolveWarningModal(false)">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="primary" @click="resolveWarningModal(true)">
            {{ $t('ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <h2>{{ $tc('product_start_temp', 2) }}</h2>
    <div v-if="customerContext.getSetting('enableDailyStartTemp')">
      <v-form v-if="loaded" id="product-start-temps-form" @submit.prevent="onSubmit">
        <v-simple-table>
          <thead>
            <tr>
              <th>{{ $t('name') }}</th>
              <th class="col-2">{{ $t('starting_temperature') }}</th>
              <th>{{ $t('current_start_temperature') }}</th>
              <th>{{ $t('last_update') }}</th>
              <th>{{ $t('by') }}</th>
              <th>
                <div class="d-flex align-center">
                  {{ $t('default_starting_temperature') }}
                  <info-modal small>
                    <v-card-title>
                      <h4>{{ $t('default_starting_temperature') }}</h4>
                    </v-card-title>
                    <v-card-text>{{ $t('default_start_temp_expiration_info') }}</v-card-text>
                  </info-modal>
                </div>
              </th>
              <th>{{ $t('min_start_temp') }}</th>
              <th>{{ $t('max_start_temp') }}</th>
              <th>{{ $t('minimum_product_temperature') }}</th>
              <th>{{ $t('maximum_product_temperature') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(productStartTemp, index) in productStartTemps" :key="index">
              <td>
                <router-link :to="`/product/${productStartTemp.product_id}`">{{ productStartTemp.product.name }}</router-link>
              </td>
              <td>
                <v-text-field
                  id="user-repeat-password"
                  v-model.number="productStartTemp.new_start_temp"
                  :error-messages="get(productStartTempErrors, index, [])"
                  :append-icon="customerContext.temperatureIcon()"/>
              </td>
              <td>
                <if-set :value="productStartTemp.start_temp">
                  <span class="font-weight-bold" :class="productStartTemp.expired ? 'danger--text' : 'text'">
                    {{ productStartTemp.start_temp }} &deg;{{ productStartTemp.temperature_scale }}
                  </span>
                </if-set>
              </td>
              <td>
                <if-set :value="productStartTemp.created">
                  <span class="font-weight-bold" :class="productStartTemp.expired ? 'danger--text' : 'text'">
                    <multi-time-display :time="productStartTemp.created" />
                  </span>
                </if-set>
              </td>
              <td>
                <span v-if="productStartTemp.latest_audit_log" :class="productStartTemp.expired ? 'expired-temperature' : 'temperature'">
                  {{ productStartTemp.latest_audit_log.user.full_name }} ({{ productStartTemp.latest_audit_log.customer.name }})
                </span>
                <span v-else class="text-muted">
                  {{ $t('pending') }}
                </span>
              </td>
              <td>
                <if-set :value="productStartTemp.product.start_temp">
                  {{ productStartTemp.product.start_temp }} &deg;{{ productStartTemp.product.temperature_scale }}
                </if-set>
              </td>
              <td>
                <if-set :value="productStartTemp.product.min_start_temp">
                  {{ productStartTemp.product.min_start_temp }} &deg;{{ productStartTemp.product.temperature_scale }}
                </if-set>
              </td>
              <td>
                <if-set :value="productStartTemp.product.max_start_temp">
                  {{ productStartTemp.product.max_start_temp }} &deg;{{ productStartTemp.product.temperature_scale }}
                </if-set>
              </td>
              <td>
                <if-set :value="productStartTemp.product.min_temp">
                  {{ productStartTemp.product.min_temp }} &deg;{{ productStartTemp.product.temperature_scale }}
                </if-set>
              </td>
              <td>
                <if-set :value="productStartTemp.product.max_temp">
                  {{ productStartTemp.product.max_temp }} &deg;{{ productStartTemp.product.temperature_scale }}
                </if-set>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-btn type="submit" class="mt-3" color="primary" @click="$gtag.event('update', {event_category: 'ProductStartTemp'})">
          <v-icon :loading="saving" class="mr-1">
            mdi-content-save</v-icon>
          {{$t('update_temperatures')}}
        </v-btn>
      </v-form>
    </div>
    <div v-else>
      {{ $t('daily_start_temps_not_enabled') }}
    </div>
  </v-container>
</template>

<script>

import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import IfSet from '@/components/shared/IfSet'
import InfoModal from '@/components/shared/InfoModal'

import { each, filter, findIndex, get, isNumber, map } from 'lodash'

export default {
  name: 'ProductStartTemps',
  components: {
    MultiTimeDisplay,
    IfSet,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    TemperatureMixin,
    BrowserUtilitiesMixin,
    ValidationMixin
  ],
  data () {
    return {
      loaded: false,
      saving: false,
      productStartTemps: [],
      showWarningModal: false,
      // The temperatures to use in the start temperature warning modal.
      warningTemps: [],
      // A function that holds the resolver for a promise created for the warning modal.
      resolveWarningModal: null
    }
  },
  validations: {
    productStartTemps: {
      $each: {
        new_start_temp: {
          validateStartTemp: (value, vm) => {
            if (value != null && vm.product) {
              return value >= vm.product.min_start_temp && value <= vm.product.max_start_temp
            }
            return true
          }
        }
      }
    }
  },
  computed: {
    productStartTempErrors () {
      return map(this.$v.productStartTemps.$each.$iter, validation => {
        if (get(validation, '$error') === true) {
          return this.$t('temp_value_error', {
            min_temp: this.formatTempHelper(validation.$model.product.min_start_temp),
            max_temp: this.formatTempHelper(validation.$model.product.max_start_temp),
            temperatureFormat: validation.$model.temperature_scale
          })
        }
        return null
      })
    }
  },
  methods: {
    get: get,
    /**
     * Small convenience function to invoke TemperatureConverterUtil on templates
     * @param temp
     */
    formatTempHelper (temp) {
      return TemperatureConverterUtil.formatTemperature(temp)
    },
    /**
     * Load the start temperature data for the currently selected customer
     */
    async fetchStartTemps () {
      this.loaded = false
      try {
        // Retrieve the product start temperature entries
        const response = await this.$orm('ProductStartTemp').findAll({
          customer_id: this.customerContext.id,
          expand: 'product,latest_audit_log,latest_audit_log.user,latest_audit_log.customer'
        })
        this.productStartTemps = response.getItems()

        // It's possible that there are no existing start temps yet for a particular product. For this reason
        // we will pull all the customer's product entries and ensure that each has an entry. If they don't, push a
        // placeholder entry into productStartTemps so it can be edited.
        const productResponse = await this.$orm('Product').findAll({
          customer_id: this.customerContext.id
        })
        each(productResponse.getItems(), product => {
          if (findIndex(this.productStartTemps, x => x.product_id === product.id) === -1) {
            this.productStartTemps.push({
              customer_id: this.customerContext.id,
              temperature_scale: this.customerContext.tempFormat(),
              product_id: product.id,
              product: product
            })
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loaded = true
      }
    },
    /**
     * Before submitting the form, show a warning modal if any of the input start temperatures are above or below the
     * product's max_temp and min_temp, respectively. Note that this is distinct from max_start_temp and
     * min_start_temp. The model validation will prevent it from exceeding the max_start_temp and min_start_temp limits,
     * this is merely to warn them that they're exceeding the normal product parameters. Some customers won't see this
     * because they have both parameters set to the same value.
     *
     * @returns Boolean Whether the process should continue
     */
    async checkWarnings () {
      const warningTemps = filter(this.productStartTemps, productStartTemp => {
        return productStartTemp != null &&
          isNumber(productStartTemp.new_start_temp) &&
          (productStartTemp.new_start_temp < productStartTemp.product.min_temp ||
            productStartTemp.new_start_temp > productStartTemp.product.max_temp)
      })
      if (warningTemps.length > 0) {
        // Show the conflicting routes/vehicle-overlap modal
        this.warningTemps = warningTemps
        this.showWarningModal = true
        // Create a promise that will be resolved when a choice is made on the warning modal.
        const modalPromise = new Promise((resolve, reject) => {
          this.resolveWarningModal = resolve
        }).finally(() => {
          this.showWarningModal = false
        })
        // Wait for the promise to be resolved before proceeding.
        if (!await modalPromise) {
          return false
        }
      }
      return true
    },
    /**
     * Submit the form.
     */
    async onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }
      // Check for warnings and show the warning modal if necessary.
      if (!await this.checkWarnings()) {
        return false
      }

      const json = []
      each(this.productStartTemps, (productStartTemp) => {
        if (productStartTemp.new_start_temp != null) {
          json.push({
            customer_id: productStartTemp.customer_id,
            product_id: productStartTemp.product_id,
            start_temp: productStartTemp.new_start_temp,
            temperature_scale: this.customerContext.tempFormat()
          })
        }
      })

      try {
        this.saving = true

        const response = await this.$httpAdapter({
          url: this.$orm('ProductStartTemp').endpoint,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          data: JSON.stringify(json)
        })

        // For each updated item, find the associated productStartTemps entry and update it.
        // Since the audit log showing who updated the temperature will not be available immediately, we ought to set
        // the associated values with the current user upon a successful save. (if we don't do this, it will show up as
        // blank and confuse the user.)
        each(response.data, productStartTemp => {
          // Find the index of the updated temperature
          const updateIndex = findIndex(this.productStartTemps, x => {
            return x.customer_id === productStartTemp.customer_id &&
              x.product_id === productStartTemp.product_id
          })
          if (updateIndex !== -1) {
            // Update the temperature and who updated it.
            this.productStartTemps[updateIndex].expired = false
            this.productStartTemps[updateIndex].new_start_temp = null
            this.productStartTemps[updateIndex].start_temp = productStartTemp.start_temp
            this.productStartTemps[updateIndex].created = productStartTemp.created
            this.productStartTemps[updateIndex].latest_audit_log = {
              user_name: this.user.full_name,
              customer_name: this.customerContext.name
            }
          }
        })
        await this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$i18n.tc('product_start_temp', 2)} ${this.$i18n.t('generic_save_success')}`
        )
      } catch (error) {
        this.handleError(error)
      } finally {
        this.saving = false
      }
    },
    /**
     * Re-fetch data if customer changes
     */
    customerContextChangeHandler () {
      this.fetchStartTemps()
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('product_start_temp', 2))
    this.fetchStartTemps()
  }
}

</script>

<style scoped lang="scss">
</style>
